import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPermission } from "src/context/permission-slice";
import { RootState } from "src/context/store";
import { fineGrainPermission } from "src/features/fields/services/openfga";

interface PermissionProps {
  module: string;
  action: string;
}

interface TupleKey {
  user: string;
  object: string;
}

const usePermission = ({ module, action }: PermissionProps) => {
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();

  const permissions: any = useSelector((state: RootState) => state.permission);

  const { idTokenDetails } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!module && !action) {
      setHasPermission(true);
      setLoading(false);
      return;
    }

    const checkPermission = async () => {
      try {
        // Create a cache key using module and action
        const cacheKey = `${module}-${action}`;
       // debugger;
        // Check if permission result exists in cache
        if (cacheKey in permissions) {
          setHasPermission(permissions[cacheKey].allowed);
          setLoading(false);
          return;
        }

        const mv_role = idTokenDetails?.["custom:mv_role"];

        // remove special characters and spacing from mv_role
        const cleanedMvRole = mv_role.replace(/[^a-zA-Z0-9]/g, "");

        const tupleKey: TupleKey = {
          user: `staff_position:${cleanedMvRole}`,
          object: `${module}:${action}`,
        };

        const response = await fineGrainPermission(tupleKey);

        if (response.status != 200) {
          throw new Error("Failed to check permission");
        }

        // Store the result in cache
        dispatch(setPermission({ module, action, allowed: response.data.tuples?.length > 0 ? true : false }));
        setHasPermission(response.data.tuples?.length > 0 ? true : false);
      } catch (err) {
        // set permission to false
        dispatch(setPermission({ module, action, allowed: false }));
        setHasPermission(false);
        setError(err instanceof Error ? err.message : "Failed to check permission");
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [module, action]);

  return { hasPermission, loading, error };
};

export default usePermission;
